import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, Grid, TextField, Button, IconButton,
  List, ListItem, ListItemText, ListItemIcon, Checkbox, Popover, Alert, CircularProgress, Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { api, purchaseReturnService, purchaseService, supplierService } from '../utils/apiService';

import PurchaseReturnItemsTable from '../components/purchase-return/PurchaseReturnItemsTable';
import PurchaseReturnPaymentSection from '../components/purchase-return/PurchaseReturnPaymentSection';
import PurchaseReturnSummary from '../components/purchase-return/PurchaseReturnSummary';
import PurchaseReturnDetails from '../components/purchase-return/PurchaseReturnDetails';
import SupplierInformation from '../components/purchase-return/SupplierInformation';
import NotesSection from '../components/purchase-return/NotesSection';

const controlStyle = {
  '& .MuiOutlinedInput-root': { borderRadius: 0, height: 36 },
  '& .MuiInputBase-input': { py: 0.75, fontSize: '0.875rem' },
  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
  '& .MuiSelect-select': { py: 0.75 }
};

const multilineStyle = {
  ...controlStyle,
  '& .MuiOutlinedInput-root': { borderRadius: 0, minHeight: 36 }
};

function PurchaseReturnForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  // Detect edit mode: if `id` param is present we are editing an existing record
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');

  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [purchaseReturn, setPurchaseReturn] = useState({
    returnNumber: '',
    date: new Date(),
    supplier: null,
    originalInvoice: '',
    reasonForReturn: '',
    items: [],
    notes: '',
    status: 'Draft',
    paymentStatus: 'Paid',
    paymentAmount: '',
    paymentReference: '',
    payments: [],
    additionalCost: '0',
    discountPercentage: '0',
    discountAmount: '0',
  });

  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [newItem, setNewItem] = useState({
    product: null,
    description: '',
    quantity: 0,
    price: 0,
    vatRate: 0,
    discount: 0,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    description: false,
    discountPerc: false,
    discountAmt: false,
    netValue: false,
    priceWithVat: true,
    vatAmount: true,
    grandTotal: true
  });

  useEffect(() => {
    // Fetch products
    const fetchProducts = async () => {
      try {
        const response = await api.get('/products');
        const prodData = response.data.data || response.data || [];
        setProducts(prodData.map(p => ({
          ...p, id: p._id || p.id, name: p.productName || p.name, price: p.purchaseRate || p.salesRate || 0,
          vat: (p.taxRate !== undefined && p.taxRate !== null) ? p.taxRate : ((p.vatRate !== undefined && p.vatRate !== null) ? p.vatRate : 15)
        })));
      } catch (err) {
        console.error('Error fetching products:', err);
      }
    };

    // Fetch suppliers
    const fetchSuppliers = async () => {
      try {
        const response = await supplierService.getSuppliers();
        const suppData = Array.isArray(response) ? response : (response?.suppliers || []);
        setSuppliers(suppData.map(s => ({ ...s, id: s._id || s.id, name: s.name, vatNumber: s.vatRegistrationNumber || s.vatNumber })));
      } catch (err) {
        console.error('Error fetching suppliers:', err);
      }
    };

    fetchProducts();
    fetchSuppliers();
  }, []);

  // Fetch next return number on initial load (new form only, mirrors SalesInvoice behaviour)
  const fetchNextReturnNumber = async () => {
    try {
      const response = await purchaseReturnService.getNextPurchaseReturnNumber();
      const nextNumber =
        response?.data?.nextInvoiceNumber ||
        response?.nextInvoiceNumber ||
        (typeof response?.data === 'string' ? response.data : '') ||
        '';
      const resolved = typeof nextNumber === 'object'
        ? (nextNumber.nextInvoiceNumber || '')
        : nextNumber;
      if (resolved) {
        setPurchaseReturn(prev => ({ ...prev, returnNumber: resolved }));
      }
    } catch (error) {
      console.error('Error fetching next return number:', error);
    }
  };

  // On mount: fetch the return number (new form only)
  useEffect(() => {
    if (!isEditMode) {
      fetchNextReturnNumber();
    }
  }, [isEditMode]);

  const productsRef = React.useRef(products);
  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  const cleanItemName = (str) => {
    if (!str || typeof str !== 'string') return '';
    const trimmed = str.trim();
    if (/^Item[\s\-_:]+(.+)/i.test(trimmed)) {
      const cleaned = trimmed.replace(/^Item[\s\-_:]+/i, '').trim();
      if (cleaned) return cleaned;
    }
    return trimmed;
  };

  const mapInvoiceItems = (rawItems, productsList = []) => {
    if (!Array.isArray(rawItems)) return [];
    return rawItems.map((item, idx) => {
      const prodId = item.productId?._id || item.productId?.id || (typeof item.productId === 'string' ? item.productId : null) || item.itemId || item.id || idx;

      const matchedProd = productsList.find(p => p.id === prodId || p._id === prodId || (p.productCode && p.productCode === item.itemCode));

      const rawName = (typeof item.product === 'object' && (item.product?.name || item.product?.productName))
        ? (item.product.name || item.product.productName)
        : (matchedProd?.name ||
          (typeof item.productId === 'object' ? (item.productId?.name || item.productId?.productName) : null) ||
          item.itemName ||
          item.productName ||
          item.name ||
          item.description ||
          item.itemCode ||
          '');

      const prodName = cleanItemName(rawName) || (matchedProd?.name ? cleanItemName(matchedProd.name) : (item.itemCode || ''));

      const unitPrice = item.unitPrice !== undefined ? item.unitPrice : (item.price !== undefined ? item.price : (matchedProd?.price || 0));
      const quantity = item.qty !== undefined ? item.qty : (item.quantity !== undefined ? item.quantity : 0);
      const vatRate = item.taxRate !== undefined ? item.taxRate : (item.vatRate !== undefined ? item.vatRate : (item.vat !== undefined ? item.vat : 15));
      const discount = item.discountPerc !== undefined ? item.discountPerc : (item.discount !== undefined ? item.discount : 0);
      const grandTotal = item.grandTotal !== undefined ? item.grandTotal : (item.totalAmount !== undefined ? item.totalAmount : (item.total !== undefined ? item.total : 0));

      return {
        id: item._id || item.itemId || item.id || idx,
        product: {
          id: prodId || matchedProd?.id || matchedProd?._id,
          productCode: item.itemCode || item.productCode || matchedProd?.productCode || '',
          price: unitPrice,
          ...(matchedProd || {}),
          name: prodName
        },
        quantity: Number(quantity) || 0,
        originalQuantity: Number(quantity) || 0,
        price: Number(unitPrice) || 0,
        vatRate: Number(vatRate) || 0,
        discount: Number(discount) || 0,
        description: item.description || '',
        total: Number(grandTotal) || 0
      };
    });
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const invoiceIdFromQuery = queryParams.get('invoiceId');
    const hasInvoiceToLoad = location.state?.selectedInvoice || invoiceIdFromQuery;

    if (hasInvoiceToLoad) {
      let isMounted = true;
      const fetchInvoiceData = async () => {
        try {
          setLoading(true);
          let nextReturnNumber = '';
          try {
            // Use /api/purchase-returns/next-invoice-number (confirmed backend endpoint)
            const nextReturnNumResponse = await purchaseReturnService.getNextPurchaseReturnNumber();
            const raw =
              nextReturnNumResponse?.data?.nextInvoiceNumber ||
              nextReturnNumResponse?.nextInvoiceNumber ||
              (typeof nextReturnNumResponse?.data === 'string' ? nextReturnNumResponse.data : '') ||
              '';
            nextReturnNumber = typeof raw === 'object' ? (raw.nextInvoiceNumber || '') : raw;
          } catch (numErr) {
            console.error('Error fetching next return number:', numErr);
          }

          let invoiceData = location.state?.selectedInvoice;
          const invoiceId = invoiceData?._id || invoiceData?.id || invoiceIdFromQuery;

          if (invoiceId && (!invoiceData || (!invoiceData.details && !invoiceData.items))) {
            const response = await purchaseService.getPurchaseById(invoiceId);
            invoiceData = response.purchase || response.data || response || invoiceData;
          }

          if (!isMounted || !invoiceData) return;

          const rawItems = invoiceData.details || invoiceData.items || [];
          const items = mapInvoiceItems(rawItems, productsRef.current);

          setSelectedInvoice({
            purchaseNumber: invoiceData.purchaseNumber || invoiceData.invoiceNo || invoiceData.number,
            issueDate: invoiceData.issueDate || invoiceData.date || invoiceData.invoiceDate,
            ...invoiceData
          });

          // Resolve supplier – fall back to "Walk-in Supplier" when supplierId is null
          const resolvedSupplier = invoiceData.supplier || invoiceData.supplierId || (
            (invoiceData.customerName || invoiceData.supplierName)
              ? {
                  name: invoiceData.customerName || invoiceData.supplierName || '',
                  code: invoiceData.customerCode || invoiceData.supplierCode || '',
                  vat: invoiceData.vat || 0,
                }
              : { id: 0, name: 'Walk-in Supplier', vatNumber: 'N/A', email: 'N/A' }
          );

          setPurchaseReturn(prev => ({
            ...prev,
            returnNumber: nextReturnNumber || prev.returnNumber,
            originalInvoice: invoiceData.purchaseNumber || invoiceData.invoiceNo || invoiceData.number,
            supplier: resolvedSupplier,
            discountAmount: String(invoiceData.discountAmt ?? invoiceData.discountAmount ?? 0),
            // discountPercentage: String(invoiceData.discountPerc ?? invoiceData.discountPercentage ?? 0),
            items
          }));
        } catch (err) {
          console.error('Error fetching invoice details:', err);
        } finally {
          if (isMounted) setLoading(false);
        }
      };

      fetchInvoiceData();
      return () => { isMounted = false; };
    }
  }, [location.state?.selectedInvoice, location.search]);

  useEffect(() => {
    if (products.length > 0) {
      setPurchaseReturn(prev => {
        if (!prev.items || prev.items.length === 0) return prev;

        let hasChanges = false;
        const updatedItems = prev.items.map(item => {
          if (item.product) {
            const prodId = item.product.id || item.product._id;
            const matched = products.find(p => p.id === prodId || p._id === prodId);
            if (matched) {
              const cleanedMatchedName = cleanItemName(matched.name);
              if (cleanedMatchedName && item.product.name !== cleanedMatchedName) {
                hasChanges = true;
                return {
                  ...item,
                  product: {
                    ...item.product,
                    ...matched,
                    name: cleanedMatchedName
                  }
                };
              }
            }
          }
          return item;
        });

        return hasChanges ? { ...prev, items: updatedItems } : prev;
      });
    }
  }, [products]);

  const calculateVAT = (items) => {
    return items.reduce((total, item) => {
      const lineTotal = item.quantity * item.price;
      const discountAmount = (lineTotal * (item.discount || 0)) / 100;
      const netValue = lineTotal - discountAmount;
      return total + ((netValue * (item.vatRate || 0)) / 100);
    }, 0);
  };

  const calculateNetAmount = (items) => {
    return items.reduce((total, item) => {
      const lineTotal = item.quantity * item.price;
      const discountAmount = (lineTotal * (item.discount || 0)) / 100;
      return total + (lineTotal - discountAmount);
    }, 0);
  };

  const calculateSubtotal = (items) => calculateNetAmount(items);
  const calculateTotal = (items) => calculateNetAmount(items) + calculateVAT(items);

  const getTotalPayments = () => purchaseReturn.payments.reduce((sum, p) => sum + p.amount, 0);
  const getRemainingBalance = () => Math.max(0, calculateTotal(purchaseReturn.items) - getTotalPayments());

  const handlePurchaseReturnChange = (updater) => {
    if (typeof updater === 'function') {
      setPurchaseReturn(prev => updater(prev));
    } else {
      setPurchaseReturn(updater);
    }
  };

  const handleInvoiceSelect = async (event, invoice) => {
    if (invoice) {
      try {
        setLoading(true);
        const invoiceId = invoice._id || invoice.id;
        let invoiceData = invoice;

        if (invoiceId && !invoice.details && !invoice.items) {
          const response = await purchaseService.getPurchaseById(invoiceId);
          invoiceData = response.purchase || response.data || response;
        }

        const rawItems = invoiceData.details || invoiceData.items || [];
        const items = mapInvoiceItems(rawItems, products);

        setSelectedInvoice({
          purchaseNumber: invoiceData.purchaseNumber || invoiceData.invoiceNo || invoiceData.number,
          issueDate: invoiceData.issueDate || invoiceData.date || invoiceData.invoiceDate,
          ...invoiceData
        });

        // Resolve supplier – fall back to "Walk-in Supplier" when supplierId is null
        const resolvedSupplier = invoiceData.supplier || invoiceData.supplierId || (
          (invoiceData.customerName || invoiceData.supplierName)
            ? {
                name: invoiceData.customerName || invoiceData.supplierName,
                code: invoiceData.customerCode || invoiceData.supplierCode,
                vat: invoiceData.vat || 0,
              }
            : { id: 0, name: 'Walk-in Supplier', vatNumber: 'N/A', email: 'N/A' }
        );

        setPurchaseReturn(prev => ({
          ...prev,
          originalInvoice: invoiceData.purchaseNumber || invoiceData.invoiceNo || invoiceData.number,
          supplier: resolvedSupplier,
          discountAmount: String(invoiceData.discountAmt ?? invoiceData.discountAmount ?? 0),
          discountPercentage: String(invoiceData.discountPerc ?? invoiceData.discountPercentage ?? 0),
          items
        }));
      } catch (err) {
        console.error('Error selecting invoice:', err);
        enqueueSnackbar('Failed to load invoice details', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedInvoice(null);
      setPurchaseReturn(prev => ({
        ...prev,
        originalInvoice: '',
        items: []
      }));
    }
  };

  const handleItemChange = (itemId, field, value) => {
    setPurchaseReturn(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'product' && value) {
            updatedItem.price = value.price !== undefined ? value.price : item.price;
            updatedItem.description = value.description !== undefined ? value.description : item.description;
            updatedItem.vatRate = value.vat !== undefined ? value.vat : (value.vatRate !== undefined ? value.vatRate : item.vatRate);
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const handleRemoveItem = (itemId) => {
    setPurchaseReturn(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const handleAddItem = () => {
    if (!newItem.product) return;
    setPurchaseReturn(prev => ({
      ...prev,
      items: [...prev.items, {
        id: Date.now(),
        product: newItem.product,
        description: newItem.description || newItem.product.description || '',
        quantity: newItem.quantity,
        price: newItem.price || newItem.product.price,
        vatRate: (newItem.vatRate !== undefined && newItem.vatRate !== null) ? newItem.vatRate : ((newItem.product?.vat !== undefined && newItem.product?.vat !== null) ? newItem.product.vat : 15),
        discount: newItem.discount,
      }]
    }));
    setNewItem({ product: null, description: '', quantity: 0, price: 0, vatRate: 0, discount: 0 });
  };

  const handleAddPayment = (paymentData) => {
    const paymentDate = paymentData.date ? new Date(paymentData.date) : new Date();
    const newPayment = {
      id: Date.now(),
      date: paymentDate,
      method: paymentData.method || 'Unknown',
      amount: parseFloat(paymentData.amount) || 0,
      reference: paymentData.reference || '',
    };
    setPurchaseReturn(prev => ({ ...prev, payments: [...prev.payments, newPayment] }));
    setPaymentAmount('');
    setPaymentReference('');
  };

  const handleRemovePayment = (paymentId) => {
    setPurchaseReturn(prev => ({ ...prev, payments: prev.payments.filter(p => p.id !== paymentId) }));
  };

  const submitPurchaseReturn = async () => {
    try {
      setLoading(true);
      if (!purchaseReturn.supplier) throw new Error('Please select a supplier');
      if (!purchaseReturn.originalInvoice) throw new Error('Reference Invoice Number is required');
      if (!purchaseReturn.reasonForReturn) throw new Error('Reason for Return is required');
      if (purchaseReturn.items.length === 0) throw new Error('Please add at least one item');

      let resolvedSupplierId = typeof purchaseReturn.supplier === 'string'
        ? purchaseReturn.supplier
        : (purchaseReturn.supplier?.id || purchaseReturn.supplier?._id);

      // Walk-in Supplier (id === 0) is allowed – send null supplierId to backend
      const isWalkInSupplier = purchaseReturn.supplier?.id === 0 || purchaseReturn.supplier?.name === 'Walk-in Supplier';

      if (!resolvedSupplierId && !isWalkInSupplier && purchaseReturn.supplier?.name) {
        const found = suppliers.find(s => s.name === purchaseReturn.supplier.name);
        if (found) {
          resolvedSupplierId = found.id || found._id;
        }
      }

      if (!isWalkInSupplier && (!resolvedSupplierId || typeof resolvedSupplierId !== 'string')) {
        throw new Error('Valid Supplier ID is missing. Please ensure the supplier exists in the system.');
      }

      const payload = {
        masterData: {
          invoiceNo: purchaseReturn.returnNumber,
          invoiceDate: purchaseReturn.date ? new Date(purchaseReturn.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          refInvoiceNo: purchaseReturn.originalInvoice,
          refInvoiceDate: selectedInvoice?.issueDate || selectedInvoice?.date || selectedInvoice?.invoiceDate || new Date().toISOString().split('T')[0],
          supplierId: isWalkInSupplier ? null : resolvedSupplierId,
          reason: purchaseReturn.reasonForReturn,
          subtotal: calculateSubtotal(purchaseReturn.items),
          discountAmount: parseFloat(purchaseReturn.discountAmount) || 0,
          taxableAmount: calculateSubtotal(purchaseReturn.items),
          totalTax: calculateVAT(purchaseReturn.items),
          totalAmount: calculateTotal(purchaseReturn.items),
          notes: purchaseReturn.notes || '',
          status: purchaseReturn.status || 'Draft',
        },
        detailData: purchaseReturn.items.map((item, idx) => {
          const qty = item.quantity || 0;
          const price = item.price || 0;
          const discount = item.discount || 0;
          const vatRate = item.vatRate || 0;

          const lineTotal = qty * price * (1 - discount / 100);
          const vatAmount = lineTotal * (vatRate / 100);
          const grandTotal = lineTotal + vatAmount;

          return {
            itemId: item.product?.id || item.product?._id,
            itemName: item.product?.name || item.description || 'Item',
            itemCode: item.product?.productCode || item.product?.itemCode || item.product?.sku || 'UNKNOWN',
            description: item.description || item.product?.name || 'Returned Item',
            qty: qty,
            unitPrice: price,
            vat: vatRate,
            vatAmount: vatAmount,
            discountPerc: discount,
            lineTotal: lineTotal,
            grandTotal: grandTotal,
            uom: (item.unit || item.product?.basicUnit || 'EA').split('(')[0].trim(),
            lineNo: idx + 1
          };
        }),
        paymentData: purchaseReturn.payments.length > 0
          ? purchaseReturn.payments.map(p => ({
            method: p.method || 'cash',
            amount: getRemainingBalance(),
            reference: p.reference || '',
            paymentDate: p.date ? new Date(p.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            paidAmt: p.amount || 0,
            balanceAmt: p.amount || 0,
            paymentStatus: getRemainingBalance() <= 0 ? 'paid' : 'partial',
            paymentMethod: p.method || 'cash',
            paymentReference: p.reference || ''
          }))
          : [
            {
              method: paymentMethod || 'cash',
              amount: getRemainingBalance(),
              reference: paymentReference || '',
              paidAmt: getRemainingBalance(),
              balanceAmt: getTotalPayments() > 0 ? getTotalPayments() : 0,
              paymentStatus: getRemainingBalance() <= 0 ? 'paid' : getTotalPayments() > 0 ? 'partial' : 'paid',
              paymentMethod: paymentMethod || 'cash',
              paymentReference: paymentReference || ''
            }
          ]
      };

      console.log('Purchase Return Payload:', payload);

      const response = await purchaseReturnService.createPurchaseReturn(payload);
      if (response) {
        enqueueSnackbar('Purchase return created successfully', { variant: 'success' });
        navigate('/purchase-return');
      }
    } catch (err) {
      console.error('Error submitting purchase return:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create purchase return';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Paper elevation={0} sx={{ p: 0.5, borderRadius: '4px', maxWidth: '1800px', margin: '0 auto', boxShadow: 'none', border: 'none' }}>

        <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/purchase-return')} sx={{ mr: 0.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}>
              <CloseIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
                New Purchase Return
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.875rem' }}>
                Create a new purchase return
              </Typography>
            </Box>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/purchase-return')} sx={{ mr: 0.5, fontSize: '0.875rem' }}>
            Cancel
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
          All fields marked with * are required.
        </Alert>

        <Grid container spacing={0.5}>
          <Grid item xs={12} md={6}>
            <PurchaseReturnDetails
              purchaseReturn={purchaseReturn}
              selectedInvoice={selectedInvoice}
              controlStyle={controlStyle}
              onPurchaseReturnChange={handlePurchaseReturnChange}
              onInvoiceSelect={handleInvoiceSelect}
              autoCodeEnabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SupplierInformation
              purchaseReturn={purchaseReturn}
              controlStyle={controlStyle}
              suppliers={suppliers}
              onPurchaseReturnChange={handlePurchaseReturnChange}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Purchase Return Items
        </Typography>

        <PurchaseReturnItemsTable
          items={purchaseReturn.items}
          onItemChange={handleItemChange}
          onRemoveItem={handleRemoveItem}
          visibleColumns={visibleColumns}
          products={products}
          onSettingsClick={(e) => {
            if (e.target && e.target.name) {
              setVisibleColumns(prev => ({ ...prev, [e.target.name]: e.target.value }));
            } else {
              setAnchorEl(e.currentTarget);
            }
          }}
          readOnly={location.state?.selectedInvoice ? true : false}
        />

        {!location.state?.selectedInvoice && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-end' }}>
            <Autocomplete
              options={products}
              getOptionLabel={(option) => option.name || ''}
              value={newItem.product}
              onChange={(e, v) => setNewItem({ ...newItem, product: v, price: v ? v.price : 0 })}
              sx={{ width: 250, mr: 2 }}
              renderInput={(params) => <TextField {...params} label="Select Product" size="small" />}
            />
            <TextField label="Quantity" value={newItem.quantity} onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 0 })} type="number" size="small" sx={{ width: 100, mr: 2 }} />
            <TextField label="Price" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })} type="number" size="small" sx={{ width: 100, mr: 2 }} />
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddItem} disabled={!newItem.product} size="small" sx={{ height: 40 }}>Add Item</Button>
          </Box>
        )}

        <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <List>
            {Object.entries(visibleColumns).map(([column, visible]) => (
              <ListItem key={column} button onClick={() => setVisibleColumns(prev => ({ ...prev, [column]: !prev[column] }))}>
                <ListItemIcon><Checkbox checked={visible} tabIndex={-1} disableRipple /></ListItemIcon>
                <ListItemText primary={column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, ' $1')} />
              </ListItem>
            ))}
          </List>
        </Popover>

        <Grid container spacing={0.5}>
          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%' }}>
              <PurchaseReturnPaymentSection
                items={purchaseReturn.items}
                payments={purchaseReturn.payments}
                paymentMethod={paymentMethod}
                paymentAmount={paymentAmount}
                paymentReference={paymentReference}
                grandTotal={calculateTotal(purchaseReturn.items)}
                totalPaid={getTotalPayments()}
                remainingBalance={getRemainingBalance()}
                onPaymentMethodChange={setPaymentMethod}
                onPaymentAmountChange={setPaymentAmount}
                onPaymentReferenceChange={setPaymentReference}
                onAddPayment={handleAddPayment}
                onRemovePayment={handleRemovePayment}
                paymentStatus={purchaseReturn.paymentStatus}
                onPaymentStatusChange={(status) => setPurchaseReturn(prev => ({ ...prev, paymentStatus: status }))}
                totalBillAmount={selectedInvoice?.totalAmount || selectedInvoice?.items?.reduce((total, item) => total + (item.total || 0), 0) || 0}
                invoiceId={selectedInvoice?.purchaseNumber || selectedInvoice?.invoiceNo}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%' }}>
              <PurchaseReturnSummary
                items={purchaseReturn.items}
                additionalCost={purchaseReturn.additionalCost}
                discountPercentage={purchaseReturn.discountPercentage}
                discountAmount={purchaseReturn.discountAmount}
                referenceInvoice={{ amount: selectedInvoice?.totalAmount || 0 }}
                onAdditionalCostChange={(value) => setPurchaseReturn({ ...purchaseReturn, additionalCost: value })}
                onDiscountPercentageChange={(percentage, amount) => setPurchaseReturn({ ...purchaseReturn, discountPercentage: percentage, discountAmount: amount })}
                onDiscountAmountChange={(amount, percentage) => setPurchaseReturn({ ...purchaseReturn, discountAmount: amount, discountPercentage: percentage })}
                calculateSubtotal={calculateSubtotal}
                calculateVat={calculateVAT}
                calculateTotal={calculateTotal}
                getTotalPayments={getTotalPayments}
                getRemainingBalance={getRemainingBalance}
              />
            </Box>
          </Grid>
        </Grid>

        <NotesSection
          value={purchaseReturn.notes}
          onChange={(e) => setPurchaseReturn({ ...purchaseReturn, notes: e.target.value })}
          multilineStyle={multilineStyle}
        />

        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" startIcon={<PrintIcon />} sx={{ fontSize: '0.875rem' }} disabled={loading}>
            Print
          </Button>
          <Button variant="contained" color="primary" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />} onClick={submitPurchaseReturn} sx={{ fontSize: '0.875rem' }} disabled={loading}>
            Submit Return
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default PurchaseReturnForm;
